<script lang="ts">
  import { buildTableRowActionSet } from "@kontextwerk/web-sdk/core/layout/table"
  import { mdiEyeOutline as tablePrimaryIcon } from "@mdi/js"
  import { onDestroy, onMount, untrack } from "svelte"
  import Table, {
    type DataRow,
    type TableGetRows,
  } from "@kontextwerk/web-sdk/ui/layout/Table.svelte"
  import { registerInvalidationHandler } from "@kontextwerk/web-sdk/core/runtime/invalidation"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import { newApproval } from "@kontextwerk/web-sdk/core/overlay/approvals"
  import {
    archivePropertiesBatch,
    fetchPropertyTable,
    restorePropertiesBatch,
    type RegistryArchiveBatchResponse,
  } from "../../../../../../../../api/kehrblatt/property-registry/properties"
  import {
    readFlatTableFilterUrlState,
    writeFlatTableFilterUrlState,
  } from "../../../../_shared/_logic/table-filter-url-state"
  import { customerRegistryActionTooltips } from "../../../../_shared/_logic/customer-registry-action-tooltips"
  import { spaNavigate } from "../../../../../../../../_shared/_logic/navigation"
  import { customerImportLocked } from "../../../../../../../_shared/_logic/customer-import-status"
  import AddressTableHeader from "./_modules/address-table-header"
  import AddressBatchActions from "./_modules/address-batch-actions"
  import AddressRowActions from "./_actions/AddressRowActions.actions"
  import AddressContactTagsCell from "./_modules/address-contact-tags-cell"
  import AddressUnitBadgesCell from "./_modules/address-unit-badges-cell"
  import AddressIdentityCell from "./_modules/address-identity-cell"
  import AddressTableFilterBar from "./_modules/address-table-filter-bar"
  import {
    customerRegistryPropertiesInvalidationTarget,
    invalidateCustomerRegistryProperties,
  } from "../../../../_shared/_logic/customer-registry-invalidation"
  import {
    ADDRESS_TABLE_PAGE_SIZE,
    addressTableColumns,
    type AddressFilterValue,
    type AddressRow,
    type DefaultAddressPrefill,
    type FilterDisplayMode,
  } from "./_shared/_logic/address-table-contract"
  import { buildAddressPropertyQuery, createDefaultAddressFilters } from "./_logic/filters"
  import {
    buildAddressRow,
    buildCustomerById,
    buildLinksByPropertyId,
    mergeCustomers,
    mergeProperties,
    mergePropertyLinks,
  } from "./_logic/rowModel"

  interface Props {
    properties?: Property[]
    propertyLinks?: PropertyCustomer[]
    organizationId: string
    customers?: Customer[]
    canEdit: boolean
    defaultAddress?: DefaultAddressPrefill | null
    refreshKey?: number | string
    onChanged?: (el: { properties: Property[]; links: PropertyCustomer[] }) => void
  }

  let {
    properties = [],
    propertyLinks = [],
    organizationId,
    customers = [],
    canEdit,
    defaultAddress = null,
    refreshKey = 0,
    onChanged,
  }: Props = $props()

  const numberFormatter = new Intl.NumberFormat("de-DE")

  const resolveAddressTableUrlStateId = () => `kehrblatt-addresses-${organizationId}`
  const addressTableUrlStateId = $derived(resolveAddressTableUrlStateId())
  const readInitialAddressUrlState = () =>
    readFlatTableFilterUrlState(resolveAddressTableUrlStateId(), [
      "search",
      "customerId",
      "unitCount",
      "contactCount",
      "unitQuery",
      "archiveScope",
    ])
  const initialAddressUrlState = untrack(readInitialAddressUrlState)
  const initialArchiveScope: ArchiveScope =
    initialAddressUrlState.filters.archiveScope === "ARCHIVED" ||
    initialAddressUrlState.filters.archiveScope === "ALL"
      ? initialAddressUrlState.filters.archiveScope
      : "ACTIVE"

  const initialFilters = (): AddressFilterValue => ({
    ...createDefaultAddressFilters(),
    ...initialAddressUrlState.filters,
    archiveScope: initialArchiveScope,
  })
  let filters = $state<AddressFilterValue>(initialFilters())
  let filterMode = $state<FilterDisplayMode>(
    initialAddressUrlState.mode === "detailed" ? "detailed" : "compact",
  )
  let importing = $state(false)
  let batchMutating = $state(false)
  let totalCount = $state(0)

  const titleWithCount = $derived.by(() => `Liegenschaften (${numberFormatter.format(totalCount)})`)
  const importLocked = $derived(Boolean(importing || $customerImportLocked))
  const lockedTooltip = customerRegistryActionTooltips.importLocked
  const canBatchArchiveProperties = $derived(canEdit && !importLocked)
  const addressColumns = $derived(addressTableColumns)
  const effectiveCustomerId = $derived(filters.customerId.trim())
  const tableRefreshKey = $derived(
    [
      refreshKey,
      filters.search.trim(),
      effectiveCustomerId,
      filters.unitCount.trim(),
      filters.contactCount.trim(),
      filters.unitQuery.trim(),
      filters.archiveScope,
    ].join("|"),
  )

  let triggerRefresh = $state<() => void | Promise<void>>(() => {})

  const writeFilterUrlState = () => {
    if (!addressTableUrlStateId) return

    writeFlatTableFilterUrlState(
      addressTableUrlStateId,
      {
        filters: { ...filters },
        mode: filterMode,
      },
      {
        filters: createDefaultAddressFilters(),
        mode: "compact",
      },
    )
  }

  const buildPropertyQuery = (offset: number, limit: number) => {
    return buildAddressPropertyQuery({
      organizationId,
      filters,
      effectiveCustomerId,
      offset,
      limit,
    })
  }

  const getRows: TableGetRows = async ({ limit, offset }) => {
    const pageSize = limit && limit > 0 ? limit : ADDRESS_TABLE_PAGE_SIZE
    const query = buildPropertyQuery(offset, pageSize)

    if (!query) return { rows: [], total: 0 }

    try {
      const response = await fetchPropertyTable(query)
      const props = response.items ?? []
      properties = mergeProperties(properties, props)

      const propertyIds = props.map((property) => property.id).filter(Boolean)
      const visibleLinks = (response.property_customers ?? []).filter((link) =>
        propertyIds.includes(link.property_id),
      )

      propertyLinks = mergePropertyLinks(propertyLinks, visibleLinks)
      customers = mergeCustomers(customers, response.customers ?? [])
      const visibleContactPoints = response.contact_points ?? []

      const visibleLinksByPropertyId = buildLinksByPropertyId(visibleLinks)
      const customerLookup = buildCustomerById(customers)
      const rows = props.map((property) =>
        buildAddressRow(
          property,
          visibleLinksByPropertyId[property.id] ?? [],
          customerLookup,
          visibleContactPoints,
        ),
      )

      return {
        rows,
        total: Number.isFinite(response.total) ? response.total : props.length,
      }
    } catch (err) {
      console.error("addresses_fetch_failed", err)
      newNotification({ class: "error", html: "Liegenschaften konnten nicht geladen werden." })

      return { rows: [], total: offset }
    }
  }

  const clearLoadedAddressState = () => {
    properties = []
    propertyLinks = []
    customers = []
  }

  const refreshAddressesFromInvalidation = async () => {
    clearLoadedAddressState()
    await Promise.resolve(triggerRefresh())
  }

  let unregisterInvalidation: (() => void) | null = null
  onMount(() => {
    unregisterInvalidation = registerInvalidationHandler(
      customerRegistryPropertiesInvalidationTarget(organizationId),
      refreshAddressesFromInvalidation,
    )
  })
  onDestroy(() => {
    unregisterInvalidation?.()
    unregisterInvalidation = null
  })

  const resolvePropertyId = (row: AddressRow) =>
    row.property?.id ?? row.links?.[0]?.property_id ?? null
  const openPropertyPage = (row: AddressRow) => {
    const propertyId = resolvePropertyId(row)

    if (!propertyId || !organizationId) return

    spaNavigate(`/organizations/${organizationId}/addresses/${propertyId}`, {
      noSearchParam: false,
    })
  }

  const selectedPropertyRows = (rows: DataRow[]) =>
    rows.map((row) => row as AddressRow).filter((row) => Boolean(row?.property?.id))

  type ArchiveMode = "archive" | "restore"

  const resultMessage = (response: RegistryArchiveBatchResponse, mode: ArchiveMode) => {
    const action = mode === "archive" ? "archiviert" : "wiederhergestellt"

    return `${response.processed_count} ${response.processed_count === 1 ? "Liegenschaft" : "Liegenschaften"} ${action}.`
  }

  const mutateProperties = async (
    ids: string[],
    mode: ArchiveMode,
    clearSelection?: () => void,
  ) => {
    if (!canEdit || importLocked || batchMutating || !ids.length) return

    await newApproval({
      title: mode === "archive" ? "Liegenschaften archivieren" : "Liegenschaften wiederherstellen",
      description:
        mode === "archive"
          ? "Die ausgewählten Liegenschaften aus neuen Suchen und Zuordnungen ausblenden? Bestehende Aufträge und Verknüpfungen bleiben unverändert."
          : "Die ausgewählten Liegenschaften wieder für neue Suchen und Zuordnungen freigeben?",
      approvalLabel: mode === "archive" ? "Ja, archivieren" : "Ja, wiederherstellen",
      declineLabel: "Abbrechen",
      approvalCallback: async () => {
        batchMutating = true
        try {
          const response =
            mode === "archive"
              ? await archivePropertiesBatch(ids)
              : await restorePropertiesBatch(ids)
          clearSelection?.()
          await invalidateCustomerRegistryProperties(organizationId)
          newNotification({
            class: response.failed_count ? "warning" : "success",
            html: resultMessage(response, mode),
          })
        } catch (err) {
          console.error(`property_batch_${mode}_failed`, err)
          newNotification({
            class: "error",
            html: `Liegenschaften konnten nicht ${mode === "archive" ? "archiviert" : "wiederhergestellt"} werden.`,
          })
        } finally {
          batchMutating = false
        }
      },
    })
  }

  const mutateProperty = async (property: Property | undefined) => {
    if (!property?.id) return

    await mutateProperties([property.id], property.archived_at ? "restore" : "archive")
  }

  const mutateAddressRow = async (addressRow: AddressRow) => {
    await mutateProperty(addressRow.property)
  }

  const mutateSelectedProperties = async (rows: DataRow[], clearSelection: () => void) => {
    if (!canBatchArchiveProperties || importLocked || batchMutating) return

    const targetRows = selectedPropertyRows(rows)

    if (!targetRows.length) {
      newNotification({ class: "info", html: "Keine Liegenschaften ausgewählt." })

      return
    }

    const archivedCount = targetRows.filter((row) => Boolean(row.property?.archived_at)).length

    if (archivedCount > 0 && archivedCount < targetRows.length) {
      newNotification({
        class: "info",
        html: "Aktive und archivierte Liegenschaften bitte getrennt auswählen.",
      })

      return
    }

    const ids = Array.from(
      new Set(targetRows.map((row) => row.property?.id).filter((id): id is string => Boolean(id))),
    )
    await mutateProperties(
      ids,
      archivedCount === targetRows.length ? "restore" : "archive",
      clearSelection,
    )
  }
</script>

<AddressTableHeader
  {organizationId}
  bind:properties
  bind:propertyLinks
  bind:customers
  bind:importing
  {canEdit}
  {defaultAddress}
  {onChanged}
  {triggerRefresh}
  title={titleWithCount}
  {filterMode}
  onFilterModeChange={(mode) => {
    filterMode = mode === "detailed" ? "detailed" : "compact"
    writeFilterUrlState()
  }}
/>
<AddressTableFilterBar
  bind:filters
  {filterMode}
  {organizationId}
  {customers}
  {importLocked}
  onChange={writeFilterUrlState}
/>
<Table
  rowActions={{
    resolve: (row) => {
      const addressRow = row as AddressRow
      const actions = AddressRowActions({
        row: addressRow,
        importLocked,
        lockedTooltip,
        onMutate: mutateAddressRow,
      })
      actions.unshift({
        id: "open-record",
        label: "Liegenschaft öffnen",
        icon: tablePrimaryIcon,
        visible: Boolean(resolvePropertyId(addressRow) && organizationId),
        onClick: () => openPropertyPage(addressRow),
      })

      return buildTableRowActionSet(actions, {
        primaryId: "open-record",
        inlineIds: addressRow.property?.archived_at ? ["action-0"] : [],
      })
    },
  }}
  columns={addressColumns}
  {getRows}
  refreshKey={tableRefreshKey}
  pageSize={ADDRESS_TABLE_PAGE_SIZE}
  fetchDebounceMs={200}
  theme="light"
  mode="table"
  urlStateId={addressTableUrlStateId}
  showActions={canEdit}
  actionsHeaderLabel="Aktionen"
  enableSelection={canBatchArchiveProperties}
  onRowsLoaded={(result) => {
    totalCount = result.total ?? 0
  }}
  setRefreshFunction={(fn) => {
    triggerRefresh = fn
  }}
>
  {#snippet batchActions({ selectedRows, clearSelection })}
    <AddressBatchActions
      {selectedRows}
      {clearSelection}
      {batchMutating}
      {importLocked}
      {lockedTooltip}
      onMutateSelected={mutateSelectedProperties}
    />
  {/snippet}

  {#snippet tableCell({ row, column })}
    {@const addressRow = row as AddressRow}
    {#if column.id === "address"}
      <AddressIdentityCell row={addressRow} />
    {:else if column.id === "unitBadges"}
      <AddressUnitBadgesCell
        rowId={addressRow.id as string | number | undefined}
        badges={addressRow.unitBadges ?? []}
      />
    {:else if column.id === "contactTags"}
      <AddressContactTagsCell
        rowId={addressRow.id as string | number | undefined}
        tags={addressRow.contactTags ?? []}
      />
    {:else}
      {row[column.id] ?? ""}
    {/if}
  {/snippet}
</Table>
